import {flatten} from 'lodash';

import {splitAt, swap} from '../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {RuleModelStrict} from '../../models/api/ruleModel';
import {decorateWithClientId} from '../editors/commons/clientIdModel';
import {DelayParam} from '../editors/commons/paramModel';
import {ClientRuleAction, RuleActionTypesEnum} from './ruleActionModel';
import {ANDConditions, RuleCondition, RuleConditionTypesEnum} from './ruleConditionModel';
import {mapToClientActions, mapToClientConditions} from './ruleEditor/recipes/helpers/recipesHelpers';
import {findRuleAction, findRuleCondition, SlaDelays, splitANDconditions} from './ruleHelpers';

export enum SlaRuleRolesEnum {
  APPLIES = 'applies',
  BREACH = 'breach',
  BREACH_TIMER = 'breach_timer',
  WARNING = 'warning',
  REMOVE_TAG_ON_REPLY = 'remove_tag_on_reply',
  REMOVE_METADATA_ON_REPLY = 'remove_metadata_on_reply',
}

interface SlaStateProps {
  slaRules: ReadonlyArray<RuleModelStrict>;
  recipeName?: string;
}

export interface SlaRecipeTimeLimits {
  breach: DelayParam;
  warning?: DelayParam;
}

export interface SlaTags {
  breach?: number;
  warning?: number;
}

export interface SlaActions {
  breach: ReadonlyArray<ClientRuleAction>;
  warning: ReadonlyArray<ClientRuleAction>;
}

interface SlaInitialRules {
  appliesTagRule?: RuleModelStrict;
  breachTimerRule?: RuleModelStrict;
  breachTagRule: RuleModelStrict;
  warningTagRule?: RuleModelStrict;
  removeTagsRule?: RuleModelStrict;
  removeMetadataRule?: RuleModelStrict;
}

export interface SlaState {
  ruleName: string;
  conditions: ANDConditions;
  active: boolean;
  tags: SlaTags;
  timeLimits: SlaRecipeTimeLimits;
  actions: SlaActions;
  initialRules: SlaInitialRules;
}

// We hide 'hasnt_tag', 'has_conversation_metadata', and 'unreplied_after'.
export const HIDDEN_FIRST_CONDITIONS: ReadonlyArray<RuleConditionTypesEnum> = [
  RuleConditionTypesEnum.HASNT_TAG,
  RuleConditionTypesEnum.HAS_CONVERSATION_METADATA,
  RuleConditionTypesEnum.UNREPLIED_AFTER,
];
const HIDDEN_FIRST_CONDITIONS_COUNT = HIDDEN_FIRST_CONDITIONS.length;

// List of Rules -> SLA state. Dual of 'toCreateSlaBundle'.
export function initialSlaState(props: SlaStateProps): SlaState {
  const {slaRules} = props;

  const appliesTagRule = maybeFindSlaRule(slaRules, SlaRuleRolesEnum.APPLIES);
  const breachTimerRule = maybeFindSlaRule(slaRules, SlaRuleRolesEnum.BREACH_TIMER);
  const breachTagRule = maybeFindBreachRule(slaRules);
  const warningTagRule = maybeFindWarningRule(slaRules);
  const removeTagsRule = maybeFindSlaRule(slaRules, SlaRuleRolesEnum.REMOVE_TAG_ON_REPLY);
  const removeMetadataRule = maybeFindSlaRule(slaRules, SlaRuleRolesEnum.REMOVE_METADATA_ON_REPLY);

  if (!breachTagRule) {
    throw new Error(
      `No rules with a breach role found among ${slaRules.map((r) => r.role || '').join(', ')}`,
    );
  }

  const appliesTagAction = appliesTagRule
    ? findRuleAction(appliesTagRule, RuleActionTypesEnum.TAG)
    : undefined;
  const breachTagAction = findRuleAction(breachTagRule, RuleActionTypesEnum.TAG);
  const warningTagAction = warningTagRule && findRuleAction(warningTagRule, RuleActionTypesEnum.TAG);

  const conditions = getConditions(breachTagRule);

  // Sanity check the first available condition
  const firstCondition = conditions[0]?.[0];
  if (firstCondition?.type !== 'inbox_in') {
    console.error(
      'First SLA conditions is not an `inbox_in`. Are the conditions ordered correctly?',
      conditions,
    );
  }

  // We want to filter out:
  // 1. Hidden rules -- Untagging SLA Applies & Warning tags are hidden/a given.
  // 2. The Breach Tag rule -- We already supply the Tag action in SlaEditorActions file, so we want to avoid a duplicate action from showing up.
  const filteredBreachRuleActions = breachTagRule.actions.filter(
    (action) =>
      !(action.type === RuleActionTypesEnum.UNTAG && action.params.tag === appliesTagAction?.params.tag) &&
      !(action.type === RuleActionTypesEnum.UNTAG && action.params.tag === warningTagAction?.params.tag) &&
      !(action.type === RuleActionTypesEnum.TAG && action.params.tag === breachTagAction?.params.tag),
  );

  const breachActions = mapToClientActions(filteredBreachRuleActions);
  const warningActions = warningTagRule?.actions
    ? mapToClientActions(warningTagRule.actions.slice(1).map(decorateWithClientId))
    : [];

  const delayBreach = findRuleCondition(breachTagRule, RuleConditionTypesEnum.UNREPLIED_AFTER);
  if (!delayBreach) {
    console.error('SLA rules does not contain a breach delay.');
  }

  const delayWarning =
    warningTagRule && findRuleCondition(warningTagRule, RuleConditionTypesEnum.UNREPLIED_AFTER);

  return {
    ruleName: props.recipeName || '',
    conditions: mapToClientConditions(conditions),
    active: breachTagRule.active,
    tags: {
      breach: breachTagAction?.params.tag,
      warning: warningTagAction?.params.tag,
    },
    actions: {
      breach: breachActions,
      warning: warningActions,
    },
    timeLimits: {
      breach: delayBreach?.params ?? SlaDelays.empty,
      warning: delayWarning?.params,
    },
    initialRules: {
      appliesTagRule,
      breachTimerRule,
      breachTagRule,
      warningTagRule,
      removeTagsRule,
      removeMetadataRule,
    },
  };
}

function maybeFindSlaRule(slaRules: ReadonlyArray<RuleModelStrict>, ruleRole: SlaRuleRolesEnum) {
  return slaRules.find((rule) => rule.role === ruleRole);
}

function maybeFindBreachRule(slaRules: ReadonlyArray<RuleModelStrict>) {
  return slaRules.find(
    (rule) => rule.role === 'breach' || rule.role === 'breach_inside_bh' || rule.role === 'breach_outside_bh',
  );
}

function maybeFindWarningRule(slaRules: ReadonlyArray<RuleModelStrict>) {
  return slaRules.find(
    (rule) =>
      rule.role === 'warning' || rule.role === 'warning_inside_bh' || rule.role === 'warning_outside_bh',
  );
}

function getConditions(breachRule: RuleModelStrict): ReadonlyArray<ReadonlyArray<RuleCondition>> {
  let conditions = splitANDconditions(breachRule);

  // Find inbox_in rule position.
  const inboxInIndex = conditions.findIndex(
    (condition) => condition[0]?.type === RuleConditionTypesEnum.INBOX_IN,
  );

  // Some history SLA rules can have a business hour condition before 'inbox_in'.
  // Swap conditions to have the inbox_in condition at index 3.
  if (inboxInIndex > HIDDEN_FIRST_CONDITIONS_COUNT) {
    conditions = swap(conditions, HIDDEN_FIRST_CONDITIONS_COUNT, inboxInIndex);
  }

  // The first AND conditions are SLA internals and not to be modified.
  // They are hidden from the user.
  const [deletedConditions, keptConditions] = splitAt(conditions, HIDDEN_FIRST_CONDITIONS_COUNT);

  // Sanity check the hidden conditions.
  const unexpectedConditions = flatten(deletedConditions).filter(
    (cond) => !HIDDEN_FIRST_CONDITIONS.includes(cond.type),
  );

  if (unexpectedConditions.length > 0) {
    const names = unexpectedConditions.map((cond) => cond.type).join(', ');
    console.error(
      `Found ${unexpectedConditions.length} unexpected implicit conditions ${names}`,
      breachRule.compoundConditions,
    );
  }

  return keptConditions;
}
